import { AuthV2Response } from "@/types/auth"
import { Timestamp } from "@/types/date"

import { addSeconds, now } from "@/lib/date-utils"
import { isClientSide } from "@/lib/is-client-side"
import { webStorage } from "@/lib/web-storage"

// This falls back to memory, which is probably fine because embedded runs as a SPA.
// We may remove it later, but for now it preserves existing behavior.
export function storeEmbeddedTokens(res: AuthV2Response): void {
  if (!isClientSide()) {
    return
  }

  if (res.auth_token) {
    webStorage.setItem("embedded.mindtrip.auth_token", res.auth_token.value)
    // Store auth token expiration as absolute timestamp
    const authTokenExpiresAt = addSeconds(now(), res.auth_token.expires_in)
    webStorage.setItem(
      "embedded.mindtrip.auth_token_expires_at",
      authTokenExpiresAt.epoch_millis.toString()
    )
  }
  if (res.refresh_token?.value) {
    webStorage.setItem(
      "embedded.mindtrip.refresh_token",
      res.refresh_token?.value
    )
    // Store refresh token expiration if available (can be null)
    if (res.refresh_token.expires_in !== null) {
      const refreshTokenExpiresAt = addSeconds(
        now(),
        res.refresh_token.expires_in
      )
      webStorage.setItem(
        "embedded.mindtrip.refresh_token_expires_at",
        refreshTokenExpiresAt.epoch_millis.toString()
      )
    }
  }
}

export function getEmbeddedAuthToken(): string | null {
  if (!isClientSide()) {
    return null
  }
  return webStorage.getItem("embedded.mindtrip.auth_token")
}

export function getEmbeddedRefreshToken(): string | null {
  if (!isClientSide()) {
    return null
  }
  return webStorage.getItem("embedded.mindtrip.refresh_token")
}

export function getEmbeddedAuthTokenExpiresAt(): Timestamp | null {
  if (!isClientSide()) {
    return null
  }
  const expiresAtStr = webStorage.getItem(
    "embedded.mindtrip.auth_token_expires_at"
  )
  return expiresAtStr ? { epoch_millis: parseInt(expiresAtStr, 10) } : null
}

export function getEmbeddedRefreshTokenExpiresAt(): Timestamp | null {
  if (!isClientSide()) {
    return null
  }
  const expiresAtStr = webStorage.getItem(
    "embedded.mindtrip.refresh_token_expires_at"
  )
  return expiresAtStr ? { epoch_millis: parseInt(expiresAtStr, 10) } : null
}

export function clearEmbeddedAuthTokens(): void {
  if (!isClientSide()) {
    return
  }
  webStorage.removeItem("embedded.mindtrip.auth")
  webStorage.removeItem("embedded.mindtrip.auth_token")
  webStorage.removeItem("embedded.mindtrip.auth_token_expires_at")
  webStorage.removeItem("embedded.mindtrip.refresh_token")
  webStorage.removeItem("embedded.mindtrip.refresh_token_expires_at")
}
